import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { API_BASE } from '../lib/api'
import { Logo } from '../components/Logo'
import '../styles/public-roadmap.css'

type InvitePreview = {
    email: string
    invitedBy: string | null
    storeNames: string[]
    message: string | null
    expiresAt: string
    state: 'pending' | 'accepted' | 'revoked' | 'expired'
}

/**
 * Landing page for an emailed invitation link. It only DESCRIBES the invitation —
 * the actual grant happens server-side on first sign-in, keyed to the invited Google
 * address, so opening this page (or forwarding the link) confers nothing.
 */
export default function InvitePage() {
    const { token } = useParams<{ token: string }>()
    const [loading, setLoading] = useState(true)
    const [invite, setInvite] = useState<InvitePreview | null>(null)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!token) { setLoading(false); setError('This invitation link is not valid.'); return }

        fetch(`${API_BASE}/api/invitations/${token}`)
            .then(async res => {
                const json = await res.json().catch(() => ({}))
                if (res.ok) setInvite(json as InvitePreview)
                else setError(json.error ?? 'This invitation link is not valid.')
            })
            .catch(() => setError('Could not reach the server. Please try again later.'))
            .finally(() => setLoading(false))
    }, [token])

    const notPending: Record<string, { icon: string; title: string; body: string }> = {
        accepted: {
            icon: '✅',
            title: 'Already accepted',
            body: 'This invitation has been used. Just sign in and your access will be waiting.'
        },
        revoked: {
            icon: '🚫',
            title: 'Invitation withdrawn',
            body: 'This invitation was revoked by an administrator. Contact them if you think that was a mistake.'
        },
        expired: {
            icon: '⏳',
            title: 'Invitation expired',
            body: 'This invitation is past its expiry date. Ask whoever invited you to send a new one.'
        }
    }

    return (
        <div className="public-roadmap-root">
            <header className="public-roadmap-header">
                <div className="public-roadmap-header-inner">
                    <div className="public-roadmap-brand">
                        <Logo height={24} />
                        <span className="public-roadmap-subtitle">Invitation</span>
                    </div>
                </div>
            </header>

            <main className="public-roadmap-content">
                <div className="confirm-card">
                    {loading && (
                        <>
                            <div className="confirm-icon"><div className="spinner" /></div>
                            <h2>Checking your invitation…</h2>
                            <p>Just a moment.</p>
                        </>
                    )}

                    {!loading && error && (
                        <>
                            <div className="confirm-icon confirm-icon--error">⚠️</div>
                            <h2>Invitation not found</h2>
                            <p>{error}</p>
                            <Link to="/login" className="btn btn-secondary" style={{ marginTop: 'var(--space-5)' }}>
                                Go to sign in
                            </Link>
                        </>
                    )}

                    {!loading && invite && invite.state !== 'pending' && (
                        <>
                            <div className="confirm-icon">{notPending[invite.state]?.icon ?? '⚠️'}</div>
                            <h2>{notPending[invite.state]?.title ?? 'Invitation unavailable'}</h2>
                            <p>{notPending[invite.state]?.body}</p>
                            <Link to="/login" className="btn btn-primary" style={{ marginTop: 'var(--space-5)' }}>
                                Go to sign in
                            </Link>
                        </>
                    )}

                    {!loading && invite && invite.state === 'pending' && (
                        <>
                            <div className="confirm-icon confirm-icon--success">🎉</div>
                            <h2>You're invited to Puresoft Dashboard</h2>
                            <p>
                                {invite.invitedBy
                                    ? <><strong>{invite.invitedBy}</strong> has invited </>
                                    : 'You have been invited '}
                                <strong>{invite.email}</strong> to join.
                            </p>

                            {invite.message && (
                                <div className="confirm-idea-title">"{invite.message}"</div>
                            )}

                            {invite.storeNames.length > 0 && (
                                <div style={{
                                    background: 'var(--color-surface)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: 'var(--space-4)',
                                    margin: 'var(--space-5) 0',
                                    textAlign: 'left'
                                }}>
                                    <div style={{
                                        fontSize: 'var(--text-xs)', textTransform: 'uppercase',
                                        letterSpacing: '0.05em', fontWeight: 600,
                                        color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)'
                                    }}>
                                        Store access you'll get
                                    </div>
                                    {invite.storeNames.map(name => (
                                        <div key={name} style={{ fontWeight: 600, padding: '2px 0' }}>{name}</div>
                                    ))}
                                </div>
                            )}

                            <p style={{ color: 'var(--color-text-secondary)' }}>
                                Sign in with the Google account for <strong>{invite.email}</strong> and your
                                access will be set up automatically.
                            </p>

                            <Link to="/login" className="btn btn-primary" style={{ marginTop: 'var(--space-5)' }}>
                                Sign in to accept
                            </Link>

                            <p style={{
                                marginTop: 'var(--space-4)',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                This invitation expires on {new Date(invite.expiresAt).toLocaleDateString(undefined, {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                })}.
                            </p>
                        </>
                    )}
                </div>
            </main>
        </div>
    )
}
